/*
 * assets/hpo-cart.js
 * ──────────────────────────────────────────────────────────────────────────
 * Page-level helpers for the standalone HpO cart page (US-008,
 * sections/hpo-cart.liquid). The full /cart page reuses the SAME global Alpine
 * `app()` store as the US-002 cart drawer — `cart`, `cart_loading`,
 * `changeCartItemQuantity`, `addCartItem`, `Shopify.formatMoney` are all
 * provided by the shipped Vite bundle (assets/main-*.js) and are in scope
 * because the whole <body> is `x-data="app()"` (layout/theme.liquid). So the
 * line-item qty stepper, remove, free-shipping progress bar and cross-sell all
 * bind straight to that store exactly like the drawer does.
 *
 * The ONE behaviour the drawer doesn't have — a per-line-item subscribe toggle
 * that flips an existing line between one-time and a native selling plan — is
 * not expressible through the bundle's changeCartItemQuantity helper (it has no
 * selling_plan param). We add it here as a tiny `hpoCartItem` Alpine component
 * that calls the native /cart/change.js endpoint directly with `selling_plan`,
 * then asks the store to re-sync via its own getCart()/refresh path. We never
 * touch the bundle or the drawer.
 *
 * Selling-plan aware + graceful when none: the toggle markup is only rendered
 * (server-side) when the line item's product has selling plans; this helper
 * just performs the change when asked.
 * ──────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // Re-sync the global store after a raw /cart/change.js call. The shipped
  // app() store exposes one of these refresh hooks depending on theme version;
  // we call whichever exists, falling back to a full reload so the page never
  // shows stale state.
  function resyncStore(component) {
    var root = component && component.$root;
    // Walk up to the Alpine root (<body x-data="app()">) data object.
    var store = window.Alpine && root ? closestAppData(root) : null;
    if (store) {
      if (typeof store.getCart === 'function') return store.getCart();
      if (typeof store.refreshCart === 'function') return store.refreshCart();
      if (typeof store.updateCart === 'function') return store.updateCart();
    }
    // Last resort: re-fetch /cart.js and patch the store.cart in place.
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (store) store.cart = data;
        else window.location.reload();
      })
      .catch(function () { window.location.reload(); });
  }

  function closestAppData(el) {
    var node = el;
    while (node) {
      if (node._x_dataStack && node._x_dataStack.length) {
        // The body app() scope is the outermost; find the stack frame that
        // actually owns `cart`.
        for (var i = 0; i < node._x_dataStack.length; i++) {
          if (node._x_dataStack[i] && 'cart' in node._x_dataStack[i]) {
            return node._x_dataStack[i];
          }
        }
      }
      node = node.parentNode;
      if (node === document) break;
    }
    return null;
  }

  document.addEventListener('alpine:init', function () {
    var Alpine = window.Alpine;
    if (!Alpine) return;

    // Per-line-item subscribe toggle. `key` is the cart line key, `sellingPlanId`
    // is the native plan to apply when subscribing (0/null = one-time).
    Alpine.data('hpoCartItem', function () {
      return {
        toggleLoading: false,
        // Resolve the selling-plan id to apply when subscribing this line.
        // Prefers the server-built product→plan map; falls back to the line's
        // already-active plan. Returns null when the product has NO plans, which
        // hides the toggle (graceful no-subscription case).
        planFor: function (item) {
          if (!item) return null;
          var map = window.__hpoCartSellingPlans || {};
          var fromMap = map[item.product_id];
          if (fromMap) return fromMap;
          if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
            return item.selling_plan_allocation.selling_plan.id;
          }
          return null;
        },
        // Flip an existing line between one-time and a selling plan. quantity
        // is preserved; the line key stays the same when only selling_plan
        // changes per the Ajax Cart API.
        setSellingPlan: function (key, quantity, sellingPlanId) {
          var self = this;
          if (this.toggleLoading) return;
          this.toggleLoading = true;
          var body = {
            id: key,
            quantity: quantity,
            selling_plan: sellingPlanId ? sellingPlanId : null,
          };
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body),
          })
            .then(function () { return resyncStore(self); })
            .catch(function () { window.location.reload(); })
            .finally(function () {
              setTimeout(function () { self.toggleLoading = false; }, 400);
            });
        },
      };
    });
  });
})();
